import { Component, computed, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { earningsAnalysisData } from '../services/earnings-analysis-data';
import { toggleSet } from '../services/toggle-set';
import { RevenueChart } from '../revenue-chart/revenue-chart';
import { EpsChart } from '../eps-chart/eps-chart';
import { V3EarningsDesktopContent } from '../v3-earnings-desktop-content/v3-earnings-desktop-content';
import { analysisBodyText } from '../analysis-data';
import { DataSources } from '../data-sources';
import { V3TabPanel } from '../v3-tab-panel/v3-tab-panel';

interface EarningsFaqItem {
  question: string;
  answer: string;
}

interface EarningsUpdateItem {
  title: string;
  text: string;
}

type UpdatePolarity = 'positive' | 'negative';

interface RawUpdateItem {
  title: string;
  body: string;
  restricted?: boolean;
}

@Component({
  selector: 'app-v3-earnings-analysis',
  imports: [NgTemplateOutlet, RevenueChart, EpsChart, V3EarningsDesktopContent, V3TabPanel],
  standalone: true,
  templateUrl: './v3-earnings-analysis.html',
  styleUrl: './v3-earnings-analysis.scss',
})
export class V3EarningsAnalysis {
  private readonly data = earningsAnalysisData();

  readonly isFinnet = this.data.isFinnet;
  readonly isDesktop = this.data.isDesktop;

  private readonly earningsAnalysis = this.data.earningsAnalysis;
  private readonly earningsTranscripts = this.data.earningsTranscripts;

  readonly dataSources = DataSources;

  // Mobile blocks; the desktop chart columns are V3EarningsDesktopContent's.
  readonly nextEarningsDate = this.data.nextEarningsDate;
  readonly isEarningsDateEnabled = this.data.isEarningsDateEnabled;
  readonly lastQuarterHighlight = this.data.title;
  readonly epsData = this.data.epsData;
  readonly epsSymbol = this.data.epsSymbol;
  readonly revenueStats = this.data.revenueStats;

  readonly developmentText = computed(() =>
    analysisBodyText(this.earningsAnalysis()?.content, 'summary'),
  );

  readonly outlookText = computed(() =>
    analysisBodyText(this.earningsAnalysis()?.content, 'outlook'),
  );

  readonly earningsCallIntro = computed(() =>
    analysisBodyText(this.earningsTranscripts()?.content, 'intro'),
  );

  private readonly updateItems = computed(() => {
    const body = (
      this.earningsAnalysis()?.content?.['updates'] as
        | { body?: { positive?: RawUpdateItem[]; negative?: RawUpdateItem[] } }
        | undefined
    )?.body;
    const toItems = (raw?: RawUpdateItem[]) =>
      Array.isArray(raw)
        ? raw.filter((i) => !i.restricted).map((i) => ({ title: i.title, text: i.body }))
        : [];
    return { positive: toItems(body?.positive), negative: toItems(body?.negative) };
  });

  readonly positiveUpdates = computed<EarningsUpdateItem[]>(() => this.updateItems().positive);
  readonly negativeUpdates = computed<EarningsUpdateItem[]>(() => this.updateItems().negative);

  readonly hasUpdates = computed(
    () => this.positiveUpdates().length > 0 || this.negativeUpdates().length > 0,
  );

  readonly updatesCount = computed(
    () => this.positiveUpdates().length + this.negativeUpdates().length,
  );

  readonly activeUpdateTab = signal<UpdatePolarity>('positive');

  readonly activeUpdates = computed(() =>
    this.activeUpdateTab() === 'positive' ? this.positiveUpdates() : this.negativeUpdates(),
  );

  selectUpdateTab(tab: UpdatePolarity) {
    this.activeUpdateTab.set(tab);
  }

  readonly faqItems = computed<EarningsFaqItem[]>(() => {
    const items = (
      this.earningsTranscripts()?.content?.['faq'] as
        | { body?: { question: string; answer: string; restricted?: boolean }[] }
        | undefined
    )?.body;
    if (!Array.isArray(items)) return [];
    return items
      .filter((i) => !i.restricted)
      .map((i) => ({ question: i.question, answer: i.answer }));
  });

  private readonly expandedFaqIndices = toggleSet();

  isFaqExpanded(index: number): boolean {
    return this.expandedFaqIndices.has(index);
  }

  toggleFaq(index: number) {
    this.expandedFaqIndices.toggle(index);
  }
}
